<template>
  <header class="header">
    <div class="header__logo">
      <img v-if="logoFile" :src="logoFile" :alt="logoAlt()" load="lazy" />
    </div>

    <div class="header__content">
      <LocaleSwitcher></LocaleSwitcher>

      <a class="help" href="javascript:void(0)" @click="showHelp = true">
        <svg-icon icon="help" class="icon--help" />{{ $t('header.help') }}</a
      >

      <transition name="modal">
        <Modal v-if="showHelp" @close="showHelp = false" :canClose="true">
          <template v-slot:body>
            <p>
              {{
                $t('header.help_modal.description', {
                  email: partnerEmail,
                  phone: partnerPhone,
                })
              }}
            </p>
          </template>
        </Modal>
      </transition>
      <a class="website" :href="'https://' + website" target="_blank">
        {{ website }}
      </a>
    </div>
  </header>
</template>

<script lang="ts">
import LocaleSwitcher from '@/components/layout/LocaleSwitcher.vue';
import Modal from '@/components/Modal.vue';
import useLocationsStore from '@/composables/store/useLocationsStore';
import usePartnerStore from '@/composables/store/usePartnerStore';
import { computed, defineComponent, ref, Ref } from 'vue';

export default defineComponent({
  name: 'PageHeader',
  components: { LocaleSwitcher, Modal },

  setup() {
    const showHelp: Ref<boolean> = ref(false);
    const {
      logoFile,
      partnerName,
      partnerEmail,
      partnerPhone,
      partnerWebsite,
    } = usePartnerStore();
    const { locations } = useLocationsStore();
    const logoAlt = () => `${partnerName.value} logo`;

    // Trim URL. Removes https://
    const trimURL = (url: string): string => {
      return url.replace(/^https?:\/\//, '');
    };

    const website = computed(() => {
      if (partnerWebsite.value) {
        return trimURL(partnerWebsite.value);
      }

      // Fallback to first location's website
      if (locations.value.length > 0 && locations.value[0].website) {
        return trimURL(locations.value[0].website);
      }
      return '';
    });
    return {
      logoFile,
      showHelp,
      logoAlt,
      website,
      partnerEmail,
      partnerPhone,
    };
  },
});
</script>

<style lang="scss" scoped>
.header {
  padding: 10px 24px;
  height: 100px;
  border-bottom: 1px solid $gallery;
  display: flex;
  background-color: $white;
  align-items: center;

  &__logo {
    max-height: 80px;

    img {
      width: 100%;
      height: 80px;
      object-fit: contain;
    }
  }

  &__content {
    display: flex;
    margin-left: auto;
    align-items: center;
  }
}

::v-deep(.language-switcher) {
  margin-right: 24px;
}

.help {
  margin-right: 24px;
  text-decoration: none;
  color: $black;
}

.website {
  display: none;
  text-decoration: none;

  @include respond-min($tablet) {
    display: block;
  }

  &:hover {
    text-decoration: underline;
  }
}

.icon {
  &--help {
    margin-right: 5px;
  }
}

@media print {
  .header {
    display: none;
  }
}
</style>
